import isPropValid from "@emotion/is-prop-valid";
import styled from "@emotion/styled";
import {
  BackgroundProperty,
  BorderColorProperty,
  BorderProperty,
  BoxShadowProperty,
  ColorProperty,
} from "csstype";
import * as React from "react";
import {
  background,
  BackgroundProps,
  border,
  borderBottom,
  BorderBottomProps,
  borderColor,
  borderLeft,
  BorderLeftProps,
  borderRadius,
  BorderRadiusProps,
  borderRight,
  BorderRightProps,
  borderStyle,
  BorderStyleProps,
  borderTop,
  BorderTopProps,
  borderWidth,
  BorderWidthProps,
  bottom,
  BottomProps,
  boxShadow,
  BoxShadowProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  left,
  LeftProps,
  margin,
  MarginProps,
  overflow,
  OverflowProps,
  position,
  PositionProps,
  right,
  RightProps,
  TLengthStyledSystem,
  top,
  TopProps,
  zIndex,
  ZIndexProps,
} from "styled-system";
import { DivProps } from "../utils/types";

type StyledSystemProps = BorderRadiusProps &
  BorderStyleProps &
  BorderWidthProps &
  BorderLeftProps &
  BorderRightProps &
  BorderTopProps &
  BorderBottomProps &
  FlexboxProps &
  LayoutProps &
  OverflowProps &
  PositionProps &
  ZIndexProps &
  LeftProps &
  RightProps &
  TopProps &
  BottomProps &
  MarginProps;

type FlexBoxProps = BoxProps;

export interface BoxProps extends StyledSystemProps, DivProps {
  /**
   * Sets the text color of the box.
   */
  color?: string | ColorProperty;
  /**
   * If true, children are placed in a row.
   */
  row?: boolean;
  /**
   * Adds spacing over and under content.
   */
  spacing?: boolean | number;
  /**
   * Adds spacing left and right of content.
   */
  indent?: boolean | number;
  style?: React.CSSProperties;
  /**
   * Adds a shadow around the box.
   */
  shadow?: string | BoxShadowProperty;
  /**
   * Sets the background of the box.
   */
  background?: string | BackgroundProperty<TLengthStyledSystem>;
  /**
   * Sets the border of the box.
   */
  border?: string | BorderProperty<TLengthStyledSystem>;
  /**
   * Sets the border color of the box.
   */
  borderColor?: string | BorderColorProperty;
  /**
   * Sets the background of the box when hovering with mouse.
   */
  hoverBackground?: string | BackgroundProperty<TLengthStyledSystem>;
  /**
   * Sets the border of the box when hovering with mouse.
   */
  hoverBorder?: string | BorderProperty<TLengthStyledSystem>;
  /**
   * Sets the background of the box when the box is in focus.
   */
  focusBackground?: string | BackgroundProperty<TLengthStyledSystem>;
  /**
   * Sets the border of the box when the box is in focus.
   */
  focusBorder?: string | BorderProperty<TLengthStyledSystem>;
  /**
   * Sets the background of the box when focus is within the box.
   */
  focusWithinBackground?: string | BackgroundProperty<TLengthStyledSystem>;
  /**
   * Sets the border of the box when focus is within the box.
   */
  focusWithinBorder?: string | BorderProperty<TLengthStyledSystem>;
}

const excludedProps = ["spacing", "indent", "width", "height", "color"];

const isExcludedWebUiProp = (propName: string) => excludedProps.indexOf(propName) !== -1;

const getPaddingRule = (props: InnerProps) =>
  props.spacing || props.indent
    ? `padding: ${numberOrZero(props.spacing) * (props.themeSpacing || 10)}px
    ${numberOrZero(props.indent) * (props.themeIndent || 10)}px;`
    : "";

type InnerProps = FlexBoxProps &
  BoxShadowProps &
  BackgroundProps & { themeSpacing: number; themeIndent: number };

const FlexBox = styled("div", {
  shouldForwardProp: (propName: string) =>
    isExcludedWebUiProp(propName) ? false : isPropValid(propName),
})<InnerProps>`
  box-sizing: border-box;
  display: ${(props: InnerProps) => props.display || "flex"};
  ${background};
  ${border};
  ${borderRight};
  ${borderLeft};
  ${borderTop};
  ${borderBottom};
  ${borderColor};
  ${borderRadius};
  ${borderStyle};
  ${borderWidth};
  ${boxShadow};
  ${({ color }: InnerProps) => (color ? `color: ${color};` : "")}
  ${flexbox};
  flex-direction: ${(props: InnerProps) => (props.row && "row") || props.flexDirection || "column"};
  ${overflow};
  ${getPaddingRule}
  ${position};
  ${layout};
  ${zIndex};
  ${left};
  ${right};
  ${top};
  ${bottom};
  ${margin};
  :hover {
    ${({ hoverBackground }: InnerProps) =>
      hoverBackground ? `background: ${hoverBackground};` : ""}
    ${({ hoverBorder }: InnerProps) => (hoverBorder ? `border: ${hoverBorder};` : "")}
  }
  :focus {
    ${({ focusBackground }: InnerProps) =>
      focusBackground ? `background: ${focusBackground};` : ""}
    ${({ focusBorder }: InnerProps) => (focusBorder ? `border: ${focusBorder};` : "")}
  }
  :focus-within {
    ${({ focusWithinBackground }: InnerProps) =>
      focusWithinBackground ? `background: ${focusWithinBackground};` : ""}
    ${({ focusWithinBorder }: InnerProps) =>
      focusWithinBorder ? `border: ${focusWithinBorder};` : ""}
  }
`;

export const Box: React.FC<BoxProps> = ({ innerRef, ...props }) => {
  return <FlexBox ref={innerRef} themeSpacing={10} themeIndent={10} {...props} />;
};

const numberOrZero = (num: number | boolean | undefined): number => {
  if (num == null) {
    return 0;
  }
  if (typeof num === "boolean") {
    return num ? 1 : 0;
  }
  return num;
};
